import { BuyerSeller } from '@ct/model';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import * as fromStatic from '../reducers/static.reducer';

export const selectStaticState = createFeatureSelector<fromStatic.State>(fromStatic.staticFeatureKey);

export const selectProducts = createSelector(selectStaticState, (state) => state.products);
export const selectCountries = createSelector(selectStaticState, (state) => state.countries);
export const selectTerminals = createSelector(selectStaticState, (state) => state.terminals);
export const selectRegions = createSelector(selectStaticState, (state) => state.regions);
export const selectCompanies = createSelector(selectStaticState, (state) => state.companies);
export const selectTradeStatuses = createSelector(selectStaticState, (state) => state.tradeStatuses);
export const selectVesselTypes = createSelector(selectStaticState, (state) => state.vesselTypes);
export const selectinformationSource = createSelector(selectStaticState, (state) => state.informationSource);
export const selectBuyersSellers = createSelector(selectCompanies, (companies) => {
  return [...companies.values()].map((comp) => {
    const buyerSeller: BuyerSeller = {
      companyName: comp.name,
      companyId: comp.id,
    };
    return buyerSeller;
  });
});
export const selectVessels = createSelector(selectStaticState, (state) => state.vessels);

/**
 * Builds a hierarchical location structure from regions, countries, and terminals.
 * Uses terminals as the governing connector since they have both regionId and countryId.
 * Also ensures countryAreaId matches between terminals and regions when present.
 */
export const selectLocationHierarchy = createSelector(
  selectRegions,
  selectCountries,
  selectTerminals,
  (regionsMap, countriesMap, terminalsMap) => {
    if (!regionsMap || !countriesMap || !terminalsMap) {
      return [];
    }

    const regions = [...regionsMap.values()];
    const countries = [...countriesMap.values()];
    const terminals = [...terminalsMap.values()];

    return regions.map((region) => ({
      id: region.id,
      name: region.name,
      type: 'region' as const,
      children: Array.from(
        new Set(
          terminals
            .filter(
              (t) =>
                t.regionId === region.id &&
                t.countryId &&
                (!region.idCountryArea || !t.countryAreaId || t.countryAreaId === region.idCountryArea),
            )
            .map((t) => t.countryId as string),
        ),
      )
        .map((countryId) => {
          const country = countriesMap.get(countryId);
          if (!country) return null;

          return {
            id: country.id,
            name: country.name,
            type: 'country' as const,
            children: terminals
              .filter(
                (t) =>
                  t.countryId === countryId &&
                  t.regionId === region.id &&
                  (!region.idCountryArea || !t.countryAreaId || t.countryAreaId === region.idCountryArea),
              )
              .map((terminal) => ({
                id: terminal.id,
                name: terminal.name,
                type: 'terminal' as const,
                children: [],
              })),
          };
        })
        .filter((c) => c !== null),
    }));
  },
);
